import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Mail, Phone, Building2, ArrowRight, CheckCircle2, Sparkles, ChevronDown, Search, QrCode, HelpCircle, X, Smartphone, Scan, Shield, Clock, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router';
import { QRCodeSVG } from 'qrcode.react';
import { useToast, ToastContainer } from './ToastNotifications';
import svgPaths from '../imports/svg-zm0k4n6jgw';
import hublerLogo from 'figma:asset/2865d8026fbc76f0286b310aae5cde48936d616e.png';
import { authService } from '../../services';
import type { SendOTPPayload, ValidateSSOPayload, QRMessage, VerifyOTPPayload, RequestLoginTokenPayload } from '../../services';
import { API_ENDPOINTS } from '../../services/api/endpoints';
import * as flags from 'country-flag-icons/react/3x2';

type LoginMethod = 'phone' | 'email' | 'sso';
type ViewMode = 'form' | 'qr';
type Step = 'login' | 'otp' | 'select-account';

interface CountryCode {
  code: string;
  name: string;
  flag: string;
  dialCode: string;
  minLength?: number;
  maxLength?: number;
}

export function LoginPageFigma() {
  const navigate = useNavigate();
  const { toasts, dismissToast, error: showError } = useToast();
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [ssoEmail, setSsoEmail] = useState('');
  const [ssoEmailError, setSsoEmailError] = useState('');
  const [countryCodes, setCountryCodes] = useState<CountryCode[]>([{ code: 'IN', name: 'India', flag: 'IN', dialCode: '+91', minLength: 10, maxLength: 10 }]);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(countryCodes[0]);
  const [countryCodesLoaded, setCountryCodesLoaded] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('form');
  const [step, setStep] = useState<Step>('login');
  const [otpIdentifier, setOtpIdentifier] = useState('');
  const [otpLoginMethod, setOtpLoginMethod] = useState<LoginMethod>('phone');
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [isAccountLoading, setIsAccountLoading] = useState(false);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [qrCode, setQrCode] = useState<string>('');
  const [qrSocket, setQrSocket] = useState<WebSocket | null>(null);
  const [qrExpiry, setQrExpiry] = useState<number>(0);
  const [qrTimedOut, setQrTimedOut] = useState<boolean>(false);
  const [qrRefreshing, setQrRefreshing] = useState<boolean>(false);

  // Content for each step
    const stepContent = [
        {
            badge: 'Welcome Back',
            heading: (
                <>
                    Ready to jump back<br />
                    <span className="text-[#257bf4]">into flow?</span>
                </>
            ),
            description: (
                <>
                    I'm Hubler, your enterprise guide. I'll help you securely<br />
                    access your workspace at FreshBite Manufacturing. Let's<br />
                    start with your professional identity.
                </>
            ),
        },
        {
            badge: 'Secure Access',
            heading: (
                <>
                    Your security is our<br />
                    <span className="text-[#257bf4]">top priority</span>
                </>
            ),
            description: (
                <>
                    We use enterprise-grade encryption and multi-factor<br />
                    authentication to keep your data safe. Sign in with<br />
                    confidence knowing your information is protected.
                </>
            ),
        },
        {
            badge: 'Join Your Team',
            heading: (
                <>
                    Powering Enterprise<br />
                    <span className="text-[#257bf4]">Process Orchestration</span>
                </>
            ),
            description: (
                <>
                    Trusted by leading organizations to automate, govern,<br />
                    and scale mission-critical workflows with confidence.
                </>
            ),
        },
    ];
  // Auto-rotate steps every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % 3);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Fetch country codes lazily when dropdown is opened
  const handleOpenCountryDropdown = () => {
    setShowCountryDropdown(!showCountryDropdown);
  };

  const handleCountryDropdownHover = () => {
    if (!countryCodesLoaded) {
      authService.getCountryCodes().then((response) => {
        if (response.success && response.result) {
          const codes = response.result.map(c => ({
            code: c.country_code,
            name: c.country_name,
            flag: c.country_code,
            dialCode: `+${c.international_dialing}`,
            minLength: c.validations?.min,
            maxLength: c.validations?.max,
          }));
          setCountryCodes(codes);
          setCountryCodesLoaded(true);
          const updated = codes.find(c => c.code === selectedCountry.code);
          if (updated) setSelectedCountry(updated);
        }
      });
    }
  };

  // OTP Timer Countdown
  useEffect(() => {
    if (step !== 'otp' || timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [step, timeLeft]);

  // QR Expiry Countdown
  useEffect(() => {
    if (qrExpiry > 0 && viewMode === 'qr' && !qrTimedOut) {
      const timer = setInterval(() => {
        setQrExpiry((prev) => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [qrExpiry, viewMode, qrTimedOut]);

  // QR Code WebSocket
  useEffect(() => {
    if (viewMode === 'qr') {
      const ws = new WebSocket(API_ENDPOINTS.qr.websocket);
      let pingInterval: NodeJS.Timeout;
      let refreshTimeout: NodeJS.Timeout;
      
      ws.onmessage = (event) => {
        console.log('QR WebSocket message received:', event.data);
        const data: QRMessage = JSON.parse(event.data);
        console.log('QR message type:', data.message_type, data);
        
        switch (data.message_type) {
          case 'code':
            console.log('QR code received, TTL:', data.ttl);
            setQrCode(data.code || '');
            setQrTimedOut(false);
            setQrRefreshing(false);
            if (data.ttl) {
              setQrExpiry(data.ttl);
              // Auto-refresh after TTL expires
              if (refreshTimeout) clearTimeout(refreshTimeout);
              refreshTimeout = setTimeout(() => {
                console.log('QR TTL expired, requesting new code');
                if (ws.readyState === WebSocket.OPEN) {
                  ws.send(JSON.stringify({ message_type: 'new_code' }));
                }
              }, data.ttl * 1000);
            }
            break;
          case 'validated':
            console.log('QR validated, token received');
            if (data.token) {
              ws.close();
              // Call login with token
              fetch('/sessions/login/', {
                method: 'POST',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRFToken': document.cookie.split('; ').find(row => row.startsWith('csrftoken2='))?.split('=')[1] || '',
                },
                body: JSON.stringify({
                  token: data.token,
                  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                }),
              })
                .then(res => res.json())
                .then((response) => {
                  if (response.success) {
                    window.location.href = '/home';
                  } else {
                    showError('Login Failed', response.error || 'Login failed');
                  }
                })
                .catch((err) => {
                  console.error('Login error:', err);
                  showError('Login Failed', 'An error occurred during login');
                });
            }
            break;
          case 'timed_out':
            console.log('QR code timed out');
            setQrCode('');
            setQrExpiry(0);
            setQrTimedOut(true);
            break;
          case 'pong':
            console.log('QR ping-pong');
            break;
        }
      };

      ws.onopen = () => {
        console.log('QR WebSocket connected');
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            console.log('Sending QR ping');
            ws.send(JSON.stringify({ message_type: 'ping' }));
          }
        }, 5000);
      };

      ws.onclose = () => {
        console.log('QR WebSocket closed');
        clearInterval(pingInterval);
        if (refreshTimeout) clearTimeout(refreshTimeout);
      };

      ws.onerror = (error) => {
        console.error('QR WebSocket error:', error);
      };

      setQrSocket(ws);
      return () => {
        ws.close();
        clearInterval(pingInterval);
        if (refreshTimeout) clearTimeout(refreshTimeout);
      };
    }
  }, [viewMode]);

  const filteredCountries = countryCodes.filter(country =>
    country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    country.dialCode.includes(countrySearch)
  );

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setOtpError('');
    if (value && index < 5) otpInputRefs.current[index + 1]?.focus();
    if (index === 5 && value && newOtp.every(d => d !== '')) handleVerifyOtp(newOtp);
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) otpInputRefs.current[index - 1]?.focus();
    else if (e.key === 'ArrowLeft' && index > 0) otpInputRefs.current[index - 1]?.focus();
    else if (e.key === 'ArrowRight' && index < 5) otpInputRefs.current[index + 1]?.focus();
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, 6);
    const newOtp = pasted.split('').concat(Array(6 - pasted.length).fill('')) as string[];
    setOtp(newOtp);
    const nextEmpty = newOtp.findIndex(d => d === '');
    if (nextEmpty !== -1) otpInputRefs.current[nextEmpty]?.focus();
    else { otpInputRefs.current[5]?.focus(); if (newOtp.every(d => d !== '')) handleVerifyOtp(newOtp); }
  };

  const handleVerifyOtp = async (otpToVerify: string[] = otp) => {
    if (otpToVerify.some(d => d === '')) { setOtpError('Please enter all 6 digits'); return; }
    setIsVerifying(true);
    setOtpError('');
    try {
      const payload: VerifyOTPPayload = { message_type: 'validate_otp', otp: Number(otpToVerify.join('')) };
      const response = await authService.verifyOTP(payload);
      if (response.success && response.result) {
        setAccounts(response.result);
        setSelectedAccount(response.result.length === 1 ? response.result[0]._id : '');
        if (response.result.length === 1) {
          handleAccountSelect(response.result[0]._id, response.result[0].user_id, response.result);
        } else {
          setStep('select-account');
        }
      } else {
        setOtpError(response.error || 'Invalid OTP. Please try again.');
        setOtp(['', '', '', '', '', '']);
        otpInputRefs.current[0]?.focus();
      }
    } catch {
      setOtpError('An error occurred. Please try again.');
      setOtp(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    setTimeLeft(120);
    setOtpError('');
    setOtp(['', '', '', '', '', '']);
    otpInputRefs.current[0]?.focus();
    try {
      const payload = otpLoginMethod === 'phone'
        ? { type: 'mobile' as const, message_type: 'send_otp' as const, mobile_number: otpIdentifier.split(' ')[1], country_code: 'IN' }
        : { type: 'email' as const, message_type: 'send_otp' as const, email: otpIdentifier };
      await authService.sendOTP(payload);
    } catch { /* silent */ }
  };

  const handleAccountSelect = async (accountId?: string, userId?: string, accountList?: any[]) => {
    const list = accountList || accounts;
    const accId = accountId || selectedAccount;
    const account = list.find((a: any) => a._id === accId);
    if (!account) return;
    setIsAccountLoading(true);
    try {
      const payload: RequestLoginTokenPayload = { message_type: 'request_login_token', account_id: account._id, user_id: userId || account.user_id };
      const response = await authService.requestLoginToken(payload);
      if (response.success && response.result) {
        const loginResponse = await authService.loginWithToken(response.result);
        if (loginResponse.success) { window.location.href = '/home'; }
        else { showError('Login Failed', loginResponse.error || 'Login failed'); setIsAccountLoading(false); }
      } else {
        showError('Login Failed', response.error || 'Failed to get login token');
        setIsAccountLoading(false);
      }
    } catch {
      showError('Error', 'An error occurred. Please try again.');
      setIsAccountLoading(false);
    }
  };

  const formatTime = (seconds: number) => `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loginMethod === 'phone') {
      if (!phoneNumber) {
        setPhoneError('Phone number is required');
        return;
      }
      if (selectedCountry.minLength && phoneNumber.length < selectedCountry.minLength) {
        setPhoneError(`Phone number must be at least ${selectedCountry.minLength} digits`);
        return;
      }
      if (selectedCountry.maxLength && phoneNumber.length > selectedCountry.maxLength) {
        setPhoneError(`Phone number must not exceed ${selectedCountry.maxLength} digits`);
        return;
      }
    }
    
    if (loginMethod === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email) {
        setEmailError('Email is required');
        return;
      }
      if (!emailRegex.test(email)) {
        setEmailError('Please enter a valid email address');
        return;
      }
    }
    
    if (loginMethod === 'sso') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!ssoEmail) {
        setSsoEmailError('Email is required');
        return;
      }
      if (!emailRegex.test(ssoEmail)) {
        setSsoEmailError('Please enter a valid email address');
        return;
      }
    }
    
    setIsSubmitting(true);
    
    try {
      if (loginMethod === 'sso') {
        const payload: ValidateSSOPayload = {
          auth_type: 'sso',
          message_type: 'validate_credentials',
          email: ssoEmail,
        };
        const response = await authService.validateSSO(payload);
        if (response.success && response.result) {
          window.location.href = response.result;
        } else {
          showError('SSO Validation Failed', response.error || 'SSO validation failed');
        }
      } else {
        const payload: SendOTPPayload = {
          type: loginMethod === 'phone' ? 'mobile' : 'email',
          message_type: 'send_otp',
          ...(loginMethod === 'phone' 
            ? { mobile_number: phoneNumber, country_code: selectedCountry.code }
            : { email }
          ),
        };
        const response = await authService.sendOTP(payload);
        if (response.success) {
          const identifier = loginMethod === 'phone' 
            ? `${selectedCountry.dialCode} ${phoneNumber}`
            : email;
          setOtpIdentifier(identifier);
          setOtpLoginMethod(loginMethod);
          setOtp(['', '', '', '', '', '']);
          setOtpError('');
          setTimeLeft(120);
          setStep('otp');
        } else {
          showError('Failed to Send OTP', response.error || 'Failed to send OTP');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      showError('Error', 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === 'otp') {
    return (
      <div className="min-h-screen h-screen overflow-hidden bg-[#f8fafc] relative flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-8 overflow-y-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-[560px]">
            <button onClick={() => setStep('login')} className="flex items-center gap-2 text-[#64748b] hover:text-[#0d131c] transition-colors mb-8 font-semibold">
              <ArrowLeft className="w-4 h-4" /> Back to login
            </button>
            <div className="bg-white rounded-3xl border-2 border-[#e2e8f0] p-12 shadow-xl">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-[rgba(37,123,244,0.1)] rounded-2xl flex items-center justify-center">
                  <Shield className="w-10 h-10 text-[#257bf4]" />
                </div>
              </div>
              <h1 className="font-extrabold text-4xl text-[#0d131c] text-center leading-[1.1] mb-3">Verify your identity</h1>
              <div className="flex items-center justify-center gap-2 text-[#64748b] mb-8">
                <p className="text-base">We've sent a 6-digit code to {otpIdentifier}</p>
              </div>
              <div className="flex gap-3 justify-center mb-6">
                {otp.map((digit, index) => (
                  <motion.input
                    key={index}
                    ref={(el) => (otpInputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={handleOtpPaste}
                    disabled={isVerifying}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className={`w-14 h-16 text-center text-2xl font-bold bg-white border-2 rounded-2xl transition-all focus:outline-none focus:ring-0 ${
                      otpError ? 'border-red-400 text-red-600' : digit ? 'border-[#257bf4] text-[#0d131c]' : 'border-[#e2e8f0] text-[#6b7280]'
                    } focus:border-[#257bf4] disabled:opacity-50`}
                    autoFocus={index === 0}
                  />
                ))}
              </div>
              {otpError && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center text-sm text-red-600 mb-6">{otpError}</motion.p>}
              <div className="flex items-center justify-between mb-8 pb-6 border-b border-[#e2e8f0]">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#64748b]" />
                  <span className="text-sm text-[#64748b]">Expires in <span className="font-bold text-[#257bf4]">{formatTime(timeLeft)}</span></span>
                </div>
                <button
                  onClick={handleResendOtp}
                  disabled={timeLeft > 105}
                  className="font-bold text-sm text-[#257bf4] hover:text-[#1e6fd9] disabled:text-[#cbd5e1] disabled:cursor-not-allowed"
                >
                  {timeLeft > 0 ? 'Resend code' : 'Send new code'}
                </button>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={() => handleVerifyOtp()}
                disabled={isVerifying || otp.some(d => d === '')}
                className="w-full bg-[#257bf4] text-white h-16 rounded-2xl font-bold shadow-[0px_25px_50px_-12px_rgba(37,123,244,0.3)] hover:bg-[#1e6fd9] transition-all disabled:opacity-50 flex items-center justify-center gap-3"
              >
                {isVerifying ? (
                  <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="w-5 h-5 border-2 border-white border-t-transparent rounded-full" />Verifying...</>
                ) : (
                  <><CheckCircle2 className="w-5 h-5" />Verify & Continue</>
                )}
              </motion.button>
            </div>
          </motion.div>
        </div>
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      </div>
    );
  }

  if (step === 'select-account') {
    return (
      <div className="min-h-screen h-screen overflow-hidden bg-[#f8fafc] relative flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-8 overflow-y-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-[560px]">
            <button onClick={() => setStep('login')} className="flex items-center gap-2 text-[#64748b] hover:text-[#0d131c] transition-colors mb-8 font-semibold">
              <ArrowLeft className="w-4 h-4" /> Back to login
            </button>
            <div className="bg-white rounded-3xl border-2 border-[#e2e8f0] p-8 shadow-xl">
              <div className="text-center mb-8">
                <img src={hublerLogo} alt="Hubler" className="h-8 object-contain mx-auto mb-6" />
                <h1 className="font-extrabold text-3xl text-[#0d131c] leading-[1.1] mb-2">Select your account</h1>
                <p className="text-base text-[#64748b]">Choose an organization to continue</p>
              </div>
              <div className="mb-6" style={{ maxHeight: '240px', overflowY: 'auto', paddingLeft: '4px', paddingRight: '12px' }}>
                <div className="space-y-3">
                  {accounts.map((account) => (
                    <motion.button
                      key={account._id}
                      onClick={() => setSelectedAccount(account._id)}
                      disabled={isAccountLoading}
                      whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
                      className={`w-full p-3 rounded-xl border-2 transition-all text-left flex items-center gap-3 ${
                        selectedAccount === account._id ? 'border-[#257bf4] bg-[#f0f7ff]' : 'border-[#e2e8f0] bg-white hover:border-[#cbd5e1]'
                      } ${isAccountLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: account.org_logo ? 'transparent' : '#257bf415' }}>
                        {account.org_logo ? <img src={account.org_logo} alt={account.org_name} className="w-full h-full object-cover rounded-xl" /> : <Building2 className="w-5 h-5" style={{ color: '#257bf4' }} />}
                      </div>
                      <div className="flex-1" style={{ minWidth: 0, overflow: 'hidden' }}>
                        <h3 className="font-bold text-sm text-[#0d131c] truncate">{account.org_name}</h3>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedAccount === account._id ? 'border-[#257bf4]' : 'border-[#cbd5e1]'}`}>
                        {selectedAccount === account._id && <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-2.5 h-2.5 rounded-full bg-[#257bf4]" />}
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={() => handleAccountSelect()}
                disabled={!selectedAccount || isAccountLoading}
                className="w-full bg-[#257bf4] text-white h-[56px] rounded-2xl font-bold shadow-[0px_25px_50px_-12px_rgba(37,123,244,0.3)] hover:bg-[#1e6fd9] transition-all disabled:opacity-50 flex items-center justify-center gap-3"
              >
                {isAccountLoading ? (
                  <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="w-5 h-5 border-2 border-white border-t-transparent rounded-full" />Loading...</>
                ) : (
                  <>Continue <ArrowRight className="w-5 h-5" /></>
                )}
              </motion.button>
              <div className="mt-6 pt-6 border-t border-[#e2e8f0]">
                <div className="bg-[#f1f5f9] rounded-xl p-4 flex items-start gap-3">
                  <Shield className="w-5 h-5 text-[#257bf4] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-sm text-[#0d131c] mb-1">Need access to another account?</h3>
                    <p className="text-xs text-[#64748b]">Contact your organization administrator to request additional access.</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#f8fafc] relative overflow-hidden">
      {/* Logo - Top Left */}
      <div className="absolute top-8 left-8 z-10">
        <img src={hublerLogo} alt="Hubler" className="h-8" />
      </div>

      <AnimatePresence mode="wait">
        {showSuccess ? (
          <motion.div
            key="success"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="flex items-center justify-center min-h-[calc(100vh-88px)]"
          >
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <CheckCircle2 className="w-10 h-10 text-white" />
              </motion.div>
              <h2 className="font-extrabold text-2xl text-[#0d131c] mb-2">Verification Sent!</h2>
              <p className="text-[#64748b]">
                {loginMethod === 'phone' && `A verification code has been sent to ${selectedCountry.dialCode} ${phoneNumber}`}
                {loginMethod === 'email' && `A verification link has been sent to ${email}`}
                {loginMethod === 'sso' && `Redirecting to your organization's SSO portal...`}
              </p>
            </div>
          </motion.div>
        ) : viewMode === 'qr' ? (
          <motion.div
            key="qr-code"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="flex items-center justify-center px-6 min-h-[calc(100vh-88px)]"
          >
            <div className="flex gap-16 items-center justify-between max-w-6xl w-full">
              {/* Left Side - Illustration */}
              <div className="flex-1 flex items-center justify-start min-w-0 max-w-[400px]">
                <div className="relative w-full aspect-square max-w-[400px]">
                  {/* Gradient Circle */}
                  <div 
                    className="absolute inset-0 rounded-full" 
                    style={{ backgroundImage: "linear-gradient(135deg, rgba(37, 123, 244, 0.1) 0%, rgba(37, 123, 244, 0) 100%)" }}
                  />
                  
                  {/* Main Card */}
                  <div className="absolute inset-[10%] bg-white rounded-[32px] border border-[#f8fafc] shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] flex flex-col items-center justify-center p-8">
                    {/* Icon */}
                    <div className="w-20 h-20 mb-6">
                      <div className="w-20 h-20 bg-[rgba(37,123,244,0.1)] rounded-full flex items-center justify-center">
                        <svg className="w-9 h-9" fill="none" viewBox="0 0 44 44">
                          <path d={svgPaths.p35e21c00} fill="#257BF4" />
                        </svg>
                      </div>
                    </div>
                    
                    {/* Placeholder lines */}
                    <div className="space-y-3">
                      <div className="bg-[#f1f5f9] h-2.5 rounded-full w-40" />
                      <div className="bg-[#f1f5f9] h-2.5 rounded-full w-28" />
                    </div>
                    
                    {/* Shield Icon */}
                    <div className="mt-5">
                      <div className="w-14 h-14 bg-[rgba(255,138,101,0.2)] rounded-2xl border-2 border-white shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.1)] flex items-center justify-center">
                        <svg className="w-[18px] h-[22px]" fill="none" viewBox="0 0 21.3333 26.6667">
                          <path d={svgPaths.p23a04600} fill="#FF8A65" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Identity Verified Badge */}
                  <div className="absolute inset-[10%_55%_75.27%_5%] bg-white rounded-2xl border border-[#f8fafc] shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.1)] flex items-center gap-2 p-3">
                    <div className="w-7 h-7 bg-[#dcfce7] rounded-lg flex items-center justify-center">
                      <svg className="w-[16px] h-[15px]" fill="none" viewBox="0 0 18.3333 17.5">
                        <path d={svgPaths.p16151b00} fill="#16A34A" />
                      </svg>
                    </div>
                    <span className="font-bold text-[10px] text-[#0d131c] uppercase">Identity Verified</span>
                  </div>

                  {/* SAML Badge */}
                  <div className="absolute inset-[70.27%_10%_15%_48%] bg-white rounded-2xl border border-[#f8fafc] shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.1)] flex items-center gap-2 p-3">
                    <div className="w-7 h-7 bg-[#dbeafe] rounded-lg flex items-center justify-center">
                      <svg className="w-[16px] h-[9px]" fill="none" viewBox="0 0 19.1667 10">
                        <path d={svgPaths.p22917200} fill="#2563EB" />
                      </svg>
                    </div>
                    <span className="font-bold text-[10px] text-[#0d131c] uppercase">SAML 2.0 Secure</span>
                  </div>
                </div>
              </div>

              {/* Right Side - QR Code */}
              <div className="flex-1 max-w-[480px]">
                {/* QR Code Card */}
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white border-2 border-[#e2e8f0] rounded-3xl p-6 relative"
                >
                  {/* QR Code */}
                  <div className="bg-white p-5 rounded-2xl border-2 border-[#f1f5f9] mb-5">
                    <div className="aspect-square w-full max-w-[240px] mx-auto rounded-xl relative overflow-hidden flex items-center justify-center" id="qrcode">
                      {qrTimedOut ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#f8fafc]">
                          {qrRefreshing ? (
                            <>
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                className="w-8 h-8 border-2 border-[#257bf4] border-t-transparent rounded-full mb-4"
                              />
                              <p className="text-sm text-[#64748b]">Generating new code...</p>
                            </>
                          ) : (
                            <>
                              <p className="text-sm text-[#64748b] mb-4">QR Code Expired</p>
                              <button
                                onClick={() => {
                                  console.log('Generate new QR code button clicked');
                                  setQrRefreshing(true);
                                  // Reconnect WebSocket if closed
                                  if (!qrSocket || qrSocket.readyState !== WebSocket.OPEN) {
                                    console.log('WebSocket closed, reconnecting...');
                                    setViewMode('form');
                                    setTimeout(() => setViewMode('qr'), 100);
                                  } else {
                                    qrSocket.send(JSON.stringify({ message_type: 'new_code' }));
                                    console.log('Sent new_code request to WebSocket');
                                  }
                                }}
                                className="px-4 py-2 bg-[#257bf4] text-white rounded-lg font-bold text-sm hover:bg-[#1e6fd9]"
                              >
                                Generate New Code
                              </button>
                            </>
                          )}
                        </div>
                      ) : qrCode ? (
                        <QRCodeSVG 
                          value={qrCode} 
                          size={240}
                          level="H"
                          includeMargin={false}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-[#0d131c]">
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="w-8 h-8 border-2 border-white border-t-transparent rounded-full"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 bg-[#dbeafe] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Smartphone className="w-3.5 h-3.5 text-[#257bf4]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-sm text-[#0d131c] mb-0.5">Open Hubler Mobile App</h3>
                        <p className="text-xs text-[#64748b]">Launch the app on your smartphone</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 bg-[#dbeafe] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Scan className="w-3.5 h-3.5 text-[#257bf4]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-sm text-[#0d131c] mb-0.5">Tap the QR Scanner</h3>
                        <p className="text-xs text-[#64748b]">Find the scanner in your account menu</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 bg-[#dbeafe] rounded-lg flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#257bf4]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-sm text-[#0d131c] mb-0.5">Scan & Authenticate</h3>
                        <p className="text-xs text-[#64748b]">Point your camera at this QR code</p>
                      </div>
                    </div>
                  </div>

                  {/* Expiry Timer */}
                  <div className="mt-5 pt-5 border-t border-[#e2e8f0] flex items-center justify-between">
                    <p className="text-xs text-[#94a3b8]">
                      QR code expires in <span className="font-bold text-[#257bf4]">{Math.floor(qrExpiry / 60)}:{(qrExpiry % 60).toString().padStart(2, '0')}</span>
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => qrSocket?.send(JSON.stringify({ message_type: 'new_code' }))}
                      className="font-bold text-xs text-[#257bf4] hover:text-[#1e6fd9] uppercase tracking-wider"
                    >
                      Refresh
                    </motion.button>
                  </div>
                </motion.div>

                {/* Back Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setViewMode('form')}
                  className="w-full mt-5 bg-[#f1f5f9] text-[#64748b] h-[56px] rounded-2xl font-bold hover:bg-[#e2e8f0] transition-all flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Use Email or Phone Instead
                </motion.button>

                {/* Footer */}
                <div className="flex items-center gap-4 mt-5 pt-5 border-t border-[#e2e8f0]">
                  <div className="flex flex-shrink-0">
                    <div className="w-7 h-7 rounded-full bg-[#e2e8f0] border-2 border-white -mr-2">
                      <div className="w-full h-full rounded-full bg-gradient-to-br from-blue-400 to-blue-600" />
                    </div>
                    <div className="w-7 h-7 rounded-full bg-[#e2e8f0] border-2 border-white -mr-2">
                      <div className="w-full h-full rounded-full bg-gradient-to-br from-green-400 to-green-600" />
                    </div>
                    <div className="w-7 h-7 rounded-full bg-[#e2e8f0] border-2 border-white">
                      <div className="w-full h-full rounded-full bg-gradient-to-br from-purple-400 to-purple-600" />
                    </div>
                  </div>
                  <p className="font-medium text-sm text-[#94a3b8] whitespace-nowrap">
                    Trusted by leading organizations worldwide.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="login"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center justify-center px-6 py-12"
          >
            <div className="flex gap-20 items-center justify-between max-w-7xl w-full">
              {/* Left Side - Illustration */}
              <div className="flex-1 flex items-center justify-start min-w-0">
                <div className="relative w-[448px] h-[448px]">
                  {/* Gradient Circle */}
                  <div 
                    className="absolute inset-0 rounded-full" 
                    style={{ backgroundImage: "linear-gradient(135deg, rgba(37, 123, 244, 0.1) 0%, rgba(37, 123, 244, 0) 100%)" }}
                  />
                  
                  {/* Main Card */}
                  <div className="absolute inset-[10%] bg-white rounded-[32px] border border-[#f8fafc] shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] flex flex-col items-center justify-center p-10">
                    {/* Icon */}
                    <div className="w-24 h-24 mb-8">
                      <div className="w-24 h-24 bg-[rgba(37,123,244,0.1)] rounded-full flex items-center justify-center">
                        <svg className="w-11 h-11" fill="none" viewBox="0 0 44 44">
                          <path d={svgPaths.p35e21c00} fill="#257BF4" />
                        </svg>
                      </div>
                    </div>
                    
                    {/* Placeholder lines */}
                    <div className="space-y-4">
                      <div className="bg-[#f1f5f9] h-3 rounded-full w-48" />
                      <div className="bg-[#f1f5f9] h-3 rounded-full w-32" />
                    </div>
                    
                    {/* Shield Icon */}
                    <div className="mt-6">
                      <div className="w-16 h-16 bg-[rgba(255,138,101,0.2)] rounded-2xl border-2 border-white shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.1)] flex items-center justify-center">
                        <svg className="w-[21.333px] h-[26.667px]" fill="none" viewBox="0 0 21.3333 26.6667">
                          <path d={svgPaths.p23a04600} fill="#FF8A65" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Identity Verified Badge */}
                  <div className="absolute inset-[10%_48.51%_75.27%_10%] bg-white rounded-2xl border border-[#f8fafc] shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.1)] flex items-center gap-2 p-3">
                    <div className="w-7 h-7 bg-[#dcfce7] rounded-lg flex items-center justify-center">
                      <svg className="w-[16px] h-[15px]" fill="none" viewBox="0 0 18.3333 17.5">
                        <path d={svgPaths.p16151b00} fill="#16A34A" />
                      </svg>
                    </div>
                    <span className="font-bold text-[10px] text-[#0d131c] uppercase">Identity Verified</span>
                  </div>

                  {/* SAML Badge */}
                  <div className="absolute inset-[70.27%_5%_15%_53.97%] bg-white rounded-2xl border border-[#f8fafc] shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.1)] flex items-center gap-2 p-3">
                    <div className="w-7 h-7 bg-[#dbeafe] rounded-lg flex items-center justify-center">
                      <svg className="w-[16px] h-[9px]" fill="none" viewBox="0 0 19.1667 10">
                        <path d={svgPaths.p22917200} fill="#2563EB" />
                      </svg>
                    </div>
                    <span className="font-bold text-[10px] text-[#0d131c] uppercase">SAML 2.0 Secure</span>
                  </div>
                </div>
              </div>

              {/* Right Side - Form */}
              <div className="flex-1 max-w-[512px]">
                {/* Progress Indicators */}
                <div className="flex gap-2 mb-7">
                  <motion.div 
                    className="h-1.5 rounded-full w-12"
                    animate={{ 
                      backgroundColor: currentStep === 0 ? '#257bf4' : '#e2e8f0' 
                    }}
                    transition={{ duration: 0.3 }}
                  />
                  <motion.div 
                    className="h-1.5 rounded-full w-12"
                    animate={{ 
                      backgroundColor: currentStep === 1 ? '#257bf4' : '#e2e8f0' 
                    }}
                    transition={{ duration: 0.3 }}
                  />
                  <motion.div 
                    className="h-1.5 rounded-full w-12"
                    animate={{ 
                      backgroundColor: currentStep === 2 ? '#257bf4' : '#e2e8f0' 
                    }}
                    transition={{ duration: 0.3 }}
                  />
                </div>

                {/* Welcome Badge */}
                <AnimatePresence mode="wait">
                  <motion.div 
                    key={currentStep}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3 }}
                    className="inline-flex bg-[rgba(37,123,244,0.1)] rounded-full px-4 py-1 mb-3"
                  >
                    <span className="font-extrabold text-xs text-[#257bf4] uppercase tracking-wider">{stepContent[currentStep].badge}</span>
                  </motion.div>
                </AnimatePresence>

                {/* Heading */}
                <AnimatePresence mode="wait">
                  <motion.h1 
                    key={`heading-${currentStep}`}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3 }}
                    className="font-extrabold text-5xl text-[#0d131c] leading-[1.1] mb-1"
                  >
                    {stepContent[currentStep].heading}
                  </motion.h1>
                </AnimatePresence>

                {/* Description */}
                <AnimatePresence mode="wait">
                  <motion.p 
                    key={`desc-${currentStep}`}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3 }}
                    className="text-lg text-[#64748b] leading-relaxed mt-4 mb-6"
                  >
                    {stepContent[currentStep].description}
                  </motion.p>
                </AnimatePresence>

                {/* Login Method Tabs */}
                <div className="flex gap-2 mb-6 bg-[#f1f5f9] p-1 rounded-xl">
                  <button
                    onClick={() => setLoginMethod('phone')}
                    className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm transition-all ${
                      loginMethod === 'phone'
                        ? 'bg-white text-[#257bf4] shadow-sm'
                        : 'text-[#64748b] hover:text-[#0d131c]'
                    }`}
                  >
                    <Phone className="w-4 h-4 inline-block mr-2" />
                    Phone
                  </button>
                  <button
                    onClick={() => setLoginMethod('email')}
                    className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm transition-all ${
                      loginMethod === 'email'
                        ? 'bg-white text-[#257bf4] shadow-sm'
                        : 'text-[#64748b] hover:text-[#0d131c]'
                    }`}
                  >
                    <Mail className="w-4 h-4 inline-block mr-2" />
                    Email
                  </button>
                  <button
                    onClick={() => setLoginMethod('sso')}
                    className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm transition-all ${
                      loginMethod === 'sso'
                        ? 'bg-white text-[#257bf4] shadow-sm'
                        : 'text-[#64748b] hover:text-[#0d131c]'
                    }`}
                  >
                    <Building2 className="w-4 h-4 inline-block mr-2" />
                    SSO
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="relative">
                    <AnimatePresence mode="wait">
                      {loginMethod === 'phone' && (
                        <motion.div
                          key="phone"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="flex gap-2">
                            {/* Country Code Dropdown */}
                            <div className="relative">
                              <button
                                type="button"
                                onMouseEnter={handleCountryDropdownHover}
                                onClick={() => handleOpenCountryDropdown()}
                                className="h-[70px] px-4 bg-white border-2 border-[#e2e8f0] rounded-2xl hover:border-[#257bf4] transition-colors flex items-center gap-2"
                              >
                                {(() => {
                                  const FlagComponent = flags[selectedCountry.flag as keyof typeof flags] as React.ComponentType<{ className?: string }>;
                                  return FlagComponent ? <FlagComponent className="w-7 h-5 rounded" /> : <span className="text-2xl">{selectedCountry.flag}</span>;
                                })()}
                                <span className="font-medium text-[#6b7280]">{selectedCountry.dialCode}</span>
                                <ChevronDown className={`w-4 h-4 text-[#94a3b8] transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
                              </button>

                              {showCountryDropdown && (
                                <>
                                  <div 
                                    className="fixed inset-0 z-10" 
                                    onClick={() => setShowCountryDropdown(false)}
                                  />
                                  <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="absolute top-full left-0 mt-2 w-80 bg-white border border-[#e2e8f0] rounded-2xl shadow-2xl overflow-hidden z-20"
                                  >
                                    <div className="p-3 border-b border-[#e2e8f0]">
                                      <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
                                        <input
                                          type="text"
                                          value={countrySearch}
                                          onChange={(e) => setCountrySearch(e.target.value)}
                                          placeholder="Search country..."
                                          className="w-full pl-10 pr-4 py-2 bg-[#f8fafc] border border-[#e2e8f0] rounded-lg text-sm text-[#0d131c] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#257bf4] focus:border-transparent"
                                        />
                                      </div>
                                    </div>
                                    <div className="max-h-64 overflow-y-auto">
                                      {filteredCountries.map((country) => (
                                        <button
                                          key={country.code}
                                          type="button"
                                          onClick={() => {
                                            setSelectedCountry(country);
                                            setShowCountryDropdown(false);
                                            setCountrySearch('');
                                          }}
                                          className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-[#f8fafc] transition-colors ${
                                            selectedCountry.code === country.code ? 'bg-[#f8fafc]' : ''
                                          }`}
                                        >
                                          {(() => {
                                            const FlagComponent = flags[country.flag as keyof typeof flags] as React.ComponentType<{ className?: string }>;
                                            return FlagComponent ? <FlagComponent className="w-6 h-4 rounded" /> : <span className="text-xl">{country.flag}</span>;
                                          })()}
                                          <div className="flex-1 text-left">
                                            <p className="text-sm text-[#0d131c]">{country.name}</p>
                                          </div>
                                          <span className="font-medium text-sm text-[#64748b]">{country.dialCode}</span>
                                        </button>
                                      ))}
                                    </div>
                                  </motion.div>
                                </>
                              )}
                            </div>

                            {/* Phone Number Input */}
                            <div className="flex-1 relative">
                              <input
                                type="tel"
                                value={phoneNumber}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '');
                                  const maxLen = selectedCountry.maxLength;
                                  const limitedValue = maxLen ? value.slice(0, maxLen) : value;
                                  setPhoneNumber(limitedValue);
                                  setPhoneError('');
                                }}
                                placeholder="000 000 0000"
                                className="w-full h-[70px] px-6 bg-white border-2 border-[#e2e8f0] rounded-2xl font-medium text-lg text-[#6b7280] placeholder-[#cbd5e1] focus:outline-none focus:ring-0 focus:border-[#257bf4] transition-all"
                              />
                              <label className="absolute -top-3 left-4 px-2 bg-[#f8fafc] font-bold text-xs text-[#257bf4]">
                                Phone Number
                              </label>
                            </div>
                          </div>
                          {phoneError && (
                            <div style={{ marginLeft: '118px', marginTop: '8px' }}>
                              <p className="" style={{ fontSize: '14px', color: '#dc2626' }}>{phoneError}</p>
                            </div>
                          )}
                        </motion.div>
                      )}

                      {loginMethod === 'email' && (
                        <motion.div
                          key="email"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.2 }}
                          className="relative"
                        >
                          <input
                            type="text"
                            value={email}
                            onChange={(e) => {
                              setEmail(e.target.value);
                              setEmailError('');
                            }}
                            placeholder="alex@company.com"
                            className="w-full h-[70px] px-6 bg-white border-2 border-[#e2e8f0] rounded-2xl font-medium text-lg text-[#6b7280] placeholder-[#cbd5e1] focus:outline-none focus:ring-0 focus:border-[#257bf4] transition-all"
                          />
                          <label className="absolute -top-3 left-4 px-2 bg-[#f8fafc] font-bold text-xs text-[#257bf4]">
                            Work Email
                          </label>
                          {emailError && (
                            <div style={{ marginTop: '8px' }}>
                              <p className="" style={{ fontSize: '14px', color: '#dc2626' }}>{emailError}</p>
                            </div>
                          )}
                        </motion.div>
                      )}

                      {loginMethod === 'sso' && (
                        <motion.div
                          key="sso"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.2 }}
                          className="relative"
                        >
                          <input
                            type="text"
                            value={ssoEmail}
                            onChange={(e) => {
                              setSsoEmail(e.target.value);
                              setSsoEmailError('');
                            }}
                            placeholder="you@yourcompany.com"
                            className="w-full h-[70px] px-6 bg-white border-2 border-[#e2e8f0] rounded-2xl font-medium text-lg text-[#6b7280] placeholder-[#cbd5e1] focus:outline-none focus:ring-0 focus:border-[#257bf4] transition-all"
                          />
                          <label className="absolute -top-3 left-4 px-2 bg-[#f8fafc] font-bold text-xs text-[#257bf4]">
                            Work Email Address
                          </label>
                          {ssoEmailError && (
                            <div style={{ marginTop: '8px' }}>
                              <p className="" style={{ fontSize: '14px', color: '#dc2626' }}>{ssoEmailError}</p>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-4">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-[#257bf4] text-white h-[70px] rounded-2xl font-bold shadow-[0px_25px_50px_-12px_rgba(37,123,244,0.3)] hover:bg-[#1e6fd9] transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                    >
                      {isSubmitting ? (
                        <>
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                          />
                          Processing...
                        </>
                      ) : (
                        <>
                          <span>Continue to Secure Sign-On</span>
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </motion.button>

                    <div className="relative group">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        type="button"
                        onClick={() => setViewMode('qr')}
                        className="bg-white border-2 border-[#e2e8f0] h-[70px] w-[70px] rounded-2xl flex items-center justify-center hover:border-[#257bf4] transition-colors group"
                      >
                        <QrCode className="w-5 h-5 text-[#64748b] group-hover:text-[#257bf4] transition-colors" />
                      </motion.button>
                      
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        <div className="bg-[#0d131c] text-white px-3 py-2 rounded-lg whitespace-nowrap">
                          <span className="text-xs font-medium">Login via QR code</span>
                        </div>
                        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-[#0d131c]"></div>
                      </div>
                    </div>
                  </div>
                </form>

                {/* Footer */}
                <div className="flex items-center gap-4 mt-6 pt-6 border-t border-[#e2e8f0]">
                  <div className="flex flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-[#e2e8f0] border-2 border-white -mr-2">
                      <div className="w-full h-full rounded-full bg-gradient-to-br from-blue-400 to-blue-600" />
                    </div>
                    <div className="w-8 h-8 rounded-full bg-[#e2e8f0] border-2 border-white -mr-2">
                      <div className="w-full h-full rounded-full bg-gradient-to-br from-green-400 to-green-600" />
                    </div>
                    <div className="w-8 h-8 rounded-full bg-[#e2e8f0] border-2 border-white">
                      <div className="w-full h-full rounded-full bg-gradient-to-br from-purple-400 to-purple-600" />
                    </div>
                  </div>
                  <p className="font-medium text-sm text-[#94a3b8] whitespace-nowrap">
                    Trusted by leading organizations worldwide.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom Footer */}
      <div className="absolute bottom-0 left-0 right-0 px-8 py-6 flex items-center justify-between border-t border-slate-200 bg-white">
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2">
            <svg className="w-[13.333px] h-[16.667px]" fill="none" viewBox="0 0 13.3333 16.6667">
              <path d={svgPaths.p2e828280} fill="#22C55E" />
            </svg>
            <span className="font-bold text-xs text-[#64748b] uppercase tracking-wider">Enterprise Encrypted</span>
          </div>
          
          <div className="flex items-center gap-2">
            <svg className="w-[13.333px] h-[17.5px]" fill="none" viewBox="0 0 13.3333 17.5">
              <path d={svgPaths.p2eed4060} fill="#3B82F6" />
            </svg>
            <span className="font-bold text-xs text-[#64748b] uppercase tracking-wider">GDPR Compliant</span>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <a href="#" className="font-medium text-xs text-[#94a3b8] hover:text-[#64748b] uppercase tracking-wider">Privacy</a>
          <a href="#" className="font-medium text-xs text-[#94a3b8] hover:text-[#64748b] uppercase tracking-wider">Terms</a>
          <a href="#" className="font-medium text-xs text-[#94a3b8] hover:text-[#64748b] uppercase tracking-wider">Security Report</a>
          <span className="text-xs text-[#cbd5e1]">© 2012-Present Hubbler Pvt. Ltd All Rights Reserved</span>
        </div>
      </div>

      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* Secured by Hubler - Above Footer */}
      <div className="absolute bottom-24 left-8">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-[#64748b] uppercase">Secured by</span>
          <div className="bg-white px-2 py-1 rounded border border-[#e2e8f0]">
            <img src={hublerLogo} alt="Hubler" className="h-3.5" />
          </div>
        </div>
      </div>
    </div>
  );
}